import { Button } from '@mui/material';
import Container from '@brands/Components/Container/Container';
import SelectInput from '@brands/Components/Inputs/SelectInput/SelectInput';
import TextField from '@brands/Components/Inputs/TextField/TextField';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { config } from '@brands/config/config';
import {
  resolveBillingPostalCode,
  tokenizeAndVerifyBillingPostalCode,
  updateDataPayment,
} from '@brands/Dashboard/MyProfile/Patient/utils/utilsFunctions';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import useWindowSize from '@brands/hooks/useWindowSize';
import { CaseType, Pricing } from '@brands/services/cases/types/ICase';
import { addPaymentProfile } from '@brands/services/identity/addPaymentProfile';
import { getDependentById } from '@brands/services/identity/getDependentById';
import { PaymentProfile } from '@brands/services/identity/types/PaymentProfile';
import { Address, Insurance, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectOrganization } from '@brands/store/selectors/organization';
import { selectOrganizationPrices } from '@brands/store/selectors/organizationPrices';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { setForm } from '@brands/store/slices/patientFormSlice';
import { profileSelectStyles } from '@brands/Utils/customStyles';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { getSecondaryColor } from '@brands/Utils/getPrimaryColor';
import { stateOptions } from '@brands/Utils/selectOptions';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button as MuiButton } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { isMobile as mobileDetect } from 'react-device-detect';
import { AiOutlineLeft } from 'react-icons/ai';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PatientNotCoveredInsuranceSubmit } from '../utils/types';
import styles from './styles.module.scss';
import { validationSchema } from './validationSchema';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';

const { locationId } = config.square;
const appId = config.square.applicationId;
type Card = {
  attach: (containerId: string) => Promise<any>;
  tokenize: () => Promise<{
    token: string;
    status: string;
    errors?: JSON;
    details?: {
      card: any;
      method: string;
      billing: any;
    };
  }>;
  status: number;
  addEventListener: (eventName: string, callback: () => void) => void;
};

const VerifyInsurance: React.FC = () => {
  const dispatch = useAppDispatch();
  const screenSize = useWindowSize();
  const isMobile = screenSize.width < 768;
  const navigate = useNavigate();
  const [card, setCard] = useState<Card>();
  const organizationPrices = useAppSelector(selectOrganizationPrices);
  const { userInfo } = useAppSelector(selectAuth);
  const roleNavigate = useRoleNavigate();
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);
  const { selectedPatient, values } = useAppSelector(selectPatientForm);
  const currentOrganizationPrices = organizationPrices?.currentOrganizationPrices;
  const { currentOrganization } = useAppSelector(selectOrganization);
  const [disablePaymentButton, setDisablePaymentButton] = useState(false);
  const [isSquareLoading, setIsSquareLoading] = useState(true);
  const [isSameAddress, setIsSameAddress] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [dependentAddress, setDependentAddress] = useState<Address>(
    userInfo.role.name === UserRoleName.Patient
      ? userInfo.address
      : (userInfo.role.name === UserRoleName.Admin ||
          userInfo.role.name === UserRoleName.SuperAdmin ||
          userInfo.role.name === UserRoleName.OrganizationAdmin) &&
          currentPatient.address,
  );
  const dataWithUpdatedInsurance = values.find((item) => item.form_question_id === 19)?.value
    ?.dataWithUpdatedInsurance as Insurance;

  useEffect(() => {
    const fetchDependentAddress = async (): Promise<void> => {
      const selectedId = Number(selectedPatient?.value);
      if (!selectedId) return;

      const isStaffRole =
        userInfo.role.name === UserRoleName.Admin ||
        userInfo.role.name === UserRoleName.SuperAdmin ||
        userInfo.role.name === UserRoleName.OrganizationAdmin;
      const isPatientManagingOther = userInfo.role.name === UserRoleName.Patient && userInfo.id !== selectedId;
      const isStaffViewingDependent =
        isStaffRole && currentPatient.guardian_id != null && !Number.isNaN(Number(currentPatient.guardian_id));

      if (!isPatientManagingOther && !isStaffViewingDependent) {
        return;
      }

      try {
        const parentUserId =
          userInfo.role.name === UserRoleName.Patient ? Number(userInfo.id) : Number(currentPatient.guardian_id);
        if (!parentUserId) {
          return;
        }
        const dependent = await getDependentById(parentUserId, selectedId, userInfo.role.name);
        setDependentAddress(dependent.address);
      } catch (error: unknown) {
        displayErrorDetails(error);
        if (currentPatient?.address) {
          setDependentAddress(currentPatient.address);
        }
      }
    };

    if (selectedPatient?.value && userInfo.id !== selectedPatient?.value) fetchDependentAddress();
  }, [selectedPatient]);

  const initSquare = async (): Promise<void> => {
    const squareDiv = document.getElementById('card-container');
    if (!squareDiv || !window.Square) {
      throw new Error('Square.js failed to load properly');
    }
    const payments = window.Square.payments(appId, locationId);
    try {
      const paymentCard = await payments.card();
      if (squareDiv.childNodes.length === 0) {
        await paymentCard.attach('#card-container');
      }
      setCard(paymentCard);
      paymentCard.addEventListener('errorClassAdded', () => {
        setDisablePaymentButton(true);
      });
      paymentCard.addEventListener('errorClassRemoved', () => {
        setDisablePaymentButton(false);
      });
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    (async (): Promise<void> => {
      setIsSquareLoading(true);
      try {
        await initSquare();
        setPaymentStatus('');
      } finally {
        setIsSquareLoading(false);
      }
    })();
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<PatientNotCoveredInsuranceSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema(isSameAddress, 'Failed')),
  });

  const displayNotCoveredContent = (): JSX.Element => {
    return (
      <div className={styles.verifyTitle}>
        <span className={styles.customSpan}>Not Covered</span>
        <div className={styles.coveredContainer}>
          <span className={`fs-unmask ${styles.priceSpan}`}>
            {' '}
            <span className={styles.dollarSign}>$</span>
            {(() => {
              const priceInCents = currentOrganizationPrices?.find((price: Pricing) =>
                price.case_types.includes(
                  values.find((item) => item.form_question_id === 20)?.value.caseType as string,
                ),
              )?.amount_after_plan;

              if (priceInCents === undefined) {
                return null;
              }

              const priceInDollars = priceInCents / 100;
              return priceInDollars === 0 ? 'Free' : `${priceInDollars.toFixed(2)}`;
            })()}
          </span>
          <div className={styles.coverageText}>
            We’re unable to confirm your insurance coverage at this time. Your plan may not include this service, so we
            recommend checking with your insurance company for details. If you’d like to proceed with a virtual visit,
            please enter your payment information below to continue.
          </div>
        </div>
        <div className={styles.creditCatdInfo}>
          <div className={`row ${styles.row}`}>
            <div className="col-md-12">
              <div className={styles.inputWrap}>
                <div>
                  <span className="fs-unmask">Name on Card</span>
                  <span className={styles.redText}> *</span>
                </div>
                <TextField
                  {...register('creditCardFullName')}
                  errors={errors}
                  name="creditCardFullName"
                  value={getValues('creditCardFullName')}
                  onChange={(e) => {
                    setValue('creditCardFullName', e.target.value, { shouldValidate: true });
                  }}
                  wrapperStyle={{ marginTop: '0', height: '100%', borderBottom: 'none' }}
                  wrapperClassName="fs-unmask"
                  inputClass={styles.profileInputClass}
                  isUnderlined={false}
                />
              </div>
            </div>
          </div>
          <div className={`row ${styles.creditCardContainer} p-row`}>
            <div>
              <span
                style={{
                  opacity: '0.5',
                  fontSize: '16px',
                }}
                className={`fs-unmask ${styles.creditCardIconsText}`}
              >
                Credit Card Information
              </span>
              <span style={{ color: '#FA5151' }}> *</span>
            </div>
            <div id="form-container">
              <div id="card-container" />
              <span> {paymentStatus} </span>
              <div className={`${styles.invalidFeedback} invalid-feedback`}>{errors.creditCardNumber?.message}</div>
            </div>
          </div>
          <span className={`fs-unmask ${styles.subTitle}`}>Billing Address</span>
          <hr />
          <div className={styles.billingAddressCheck}>
            <input
              className="form-check-input fs-unmask"
              type="checkbox"
              id="isAdditionalInsurance"
              checked={isSameAddress}
              onChange={(event) => {
                setIsSameAddress(event.target.checked);
              }}
            />
            <label
              className={`form-check-label fs-unmask ${styles.billingAddressCheckLabel}`}
              htmlFor="isAdditionalInsurance"
            >
              Check here if your billing address is different than your home address
            </label>
          </div>
          {isSameAddress ? (
            <>
              <div className={`row ${styles.row}`}>
                <div className="col-md-12">
                  <div className={styles.inputWrap}>
                    <div>
                      <span className="fs-unmask">Street Address</span>
                      <span className={styles.redText}> *</span>
                    </div>
                    <TextField
                      {...register('creditCardAddress1')}
                      errors={errors}
                      name="creditCardAddress1"
                      value={getValues('creditCardAddress1')}
                      onChange={(e) => {
                        setValue('creditCardAddress1', e.target.value, {
                          shouldValidate: true,
                        });
                      }}
                      wrapperStyle={{ marginTop: '0', height: '100%', borderBottom: 'none' }}
                      wrapperClassName="fs-exclude"
                      inputClass={styles.profileInputClass}
                      isUnderlined={false}
                    />
                  </div>
                </div>
              </div>
              <div className={`row ${styles.row}`}>
                <div className="col-md-12">
                  <div className={styles.inputWrap}>
                    <span className="fs-unmask">Apt, suite, building (optional)</span>
                    <TextField
                      {...register('creditCardAddress2')}
                      errors={errors}
                      name="creditCardAddress2"
                      value={getValues('creditCardAddress2')}
                      onChange={(e) => {
                        setValue('creditCardAddress2', e.target.value, {
                          shouldValidate: true,
                        });
                      }}
                      wrapperStyle={{ marginTop: '0', height: '100%', borderBottom: 'none' }}
                      wrapperClassName="fs-exclude"
                      inputClass={styles.profileInputClass}
                      isUnderlined={false}
                    />
                  </div>
                </div>
              </div>
              <div className={`row ${styles.row}`}>
                <div className="col-md-4">
                  <div className={styles.inputWrap}>
                    <div>
                      <span className="fs-unmask">City</span>
                      <span className={styles.redText}> *</span>
                    </div>
                    <TextField
                      {...register('creditCardCity')}
                      errors={errors}
                      name="creditCardCity"
                      value={getValues('creditCardCity')}
                      onChange={(e) => {
                        setValue('creditCardCity', e.target.value, {
                          shouldValidate: true,
                        });
                      }}
                      wrapperStyle={{ marginTop: '0', height: '100%', borderBottom: 'none' }}
                      wrapperClassName="fs-exclude"
                      inputClass={styles.profileInputClass}
                      isUnderlined={false}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className={styles.inputWrap} data-validate="Choose State">
                    <div>
                      <span className="fs-unmask">State</span>
                      <span className={styles.redText}> *</span>
                    </div>
                    <SelectInput
                      {...register('creditCardState')}
                      containerClass="mt-0 fs-exclude"
                      name="creditCardState"
                      options={stateOptions}
                      style={profileSelectStyles}
                      errors={errors}
                      selectedValue={getValues('creditCardState')}
                      onChange={(value) => {
                        setValue('creditCardState', value?.value as string, {
                          shouldValidate: true,
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className={styles.inputWrap}>
                    <div>
                      <span className="fs-unmask">Zip Code</span>
                      <span className={styles.redText}> *</span>
                    </div>
                    <TextField
                      {...register('creditCardZipCode')}
                      errors={errors}
                      name="creditCardZipCode"
                      value={getValues('creditCardZipCode')}
                      onChange={(e) => {
                        setValue('creditCardZipCode', e.target.value, {
                          shouldValidate: true,
                        });
                      }}
                      wrapperStyle={{ marginTop: '0', height: '100%', borderBottom: 'none' }}
                      wrapperClassName="fs-exclude"
                      inputClass={styles.profileInputClass}
                      isUnderlined={false}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <span className="fs-exclude">
              {userInfo.id === Number(selectedPatient?.value)
                ? `${userInfo?.address?.address1} ${userInfo?.address?.city} ${userInfo?.address?.state} ${userInfo?.address?.zip_code}`
                : `${dependentAddress?.address1} ${dependentAddress?.city} ${dependentAddress?.state} ${dependentAddress?.zip_code}`}
            </span>
          )}
        </div>
      </div>
    );
  };

  const onInvalid = (e: any): void => {
    const error = Object.values(e)[0] as any;
    if (error) {
      toast.error(error.message as string, { className: 'fs-unmask' });
    }
  };

  const onSubmit = async (data: PatientNotCoveredInsuranceSubmit): Promise<void> => {
    setDisablePaymentButton(true);
    if (card) {
      try {
        const tokenResult = await tokenizeAndVerifyBillingPostalCode(
          card,
          resolveBillingPostalCode(data, userInfo, isSameAddress, dependentAddress, Number(currentPatient?.id)),
        );
        if (tokenResult.status === 'OK') {
          const dataWithUpdatedPayment = updateDataPayment(
            data,
            tokenResult,
            userInfo,
            isSameAddress,
            dependentAddress,
            Number(currentPatient?.id),
            Number(currentPatient.role.name === 'Dependent' ? currentPatient?.guardian_id : currentPatient?.id),
          );
          const res = await addPaymentProfile(dataWithUpdatedPayment as PaymentProfile, userInfo.role.name);
          const paymentId = res.id!;
          const payload = [...values!];
          const filteredPayload = payload.filter((item) => item.form_question_id !== 19);
          filteredPayload.push({
            form_question_id: 19,
            value: {
              paymentId: paymentId ?? '',
              insuranceId: '',
              secondaryInsuranceId: '',
              addCC: false,
              addSecondaryInsurance: false,
              dataWithUpdatedInsurance: undefined,
            },
          });
          dispatch(setForm(filteredPayload));
        } else {
          toast.error('Please Enter your Credit Card information correctly', { className: 'fs-unmask' });
          return;
        }
      } catch (error: unknown) {
        displayErrorDetails(error);
        return;
      } finally {
        setDisablePaymentButton(false);
      }
    }
    roleNavigate('/confirm-payment');
    setDisablePaymentButton(false);
  };

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
      }}
      childrenStyle={{ zIndex: '2' }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={styles.verifyInsuranceFormContainer}>
        <PatientIntakeNavigation progress={96} />
        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          {displayNotCoveredContent()}
          <div className={styles.paymentSectionButtonDiv}>
            <Button
              variant="text"
              type="button"
              className={`fs-unmask ${styles.backBtnSticky}`}
              onClick={() => navigate(-1)}
              data-testid="confirm-payment-back"
            >
              <AiOutlineLeft /> Back
            </Button>
            <Button
              type="submit"
              className={`fs-unmask ${styles.nextBtnSticky}`}
              disabled={isSquareLoading || !card || disablePaymentButton}
              onTouchStart={isMobile || mobileDetect ? handleSubmit(onSubmit, onInvalid) : undefined}
            >
              Next
            </Button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default VerifyInsurance;
